import * as React from "react"

import type { Row } from "../../generated/schema.ts"

export function useHasChannelPermission(props: {
  account: Pick<Row.account, "id" | "role"> | undefined
  channelId: string
  channelMember:
    | Pick<Row.channel_member, "account_id" | "channel_id">
    | undefined
  channelPermissions: readonly Pick<
    Row.channel_permission,
    "permission_id" | "scope"
  >[]
  organizationMember:
    | Pick<Row.organization_member, "account_id" | "organization_id">
    | undefined
  repository:
    | Pick<Row.repository, "account_id" | "id" | "organization_id">
    | undefined
  repositoryMember:
    | Pick<Row.repository_member, "account_id" | "repository_id">
    | undefined
  permissionId: Row.channel_permission["permission_id"]
}) {
  return React.useMemo(() => {
    if (!props.repository) return null // TODO: handle zero loading states properly

    if (props.account?.role === "crew") return true
    if (
      props.repository &&
      props.account &&
      props.repository.account_id === props.account.id
    )
      return true

    const channelPermissions = props.channelPermissions.filter(
      (channelPermission) =>
        channelPermission.permission_id === props.permissionId,
    )

    // check everyone, channel, repository, organization scope, where previous scope overrides next scope
    // e.g. channel_member without "channel" scope wins over organization_member with "organization" scope
    const everyoneScope = channelPermissions.find(
      (channelPermission) => channelPermission.scope === "everyone",
    )
    if (everyoneScope) return true
    if (!props.account) return false

    const channelScope = channelPermissions.find(
      (channelPermission) => channelPermission.scope === "channel",
    )
    const channelMember =
      props.channelMember?.account_id === props.account.id &&
      props.channelMember?.channel_id === props.channelId
    if (channelScope && channelMember) return true

    const repositoryScope = channelPermissions.find(
      (channelPermission) => channelPermission.scope === "repository",
    )
    const repositoryMember =
      props.repositoryMember?.account_id === props.account.id &&
      props.repositoryMember?.repository_id === props.repository?.id
    if (repositoryScope && repositoryMember) {
      if (channelMember) return false
      return true
    }

    const organizationScope = channelPermissions.find(
      (channelPermission) => channelPermission.scope === "organization",
    )
    const organizationMember =
      props.organizationMember?.account_id === props.account.id &&
      props.organizationMember?.organization_id ===
        props.repository?.organization_id
    if (organizationScope && organizationMember) {
      if (repositoryMember) return false
      return true
    }

    return false
  }, [
    props.account,
    props.channelId,
    props.channelMember,
    props.channelPermissions,
    props.organizationMember,
    props.permissionId,
    props.repository,
    props.repositoryMember,
  ])
}
